import React, { useEffect, useRef } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { addToSelected, setSelected, showContextMenu } from 'redux/mode/actions';
import { getFrameIds } from 'redux/mode/util';
import { Pick } from 'redux/mode/classes';
import { Frame } from 'dragonmouse-shared/interfaces';
import * as Base from '../styles';
import * as S from './styles';
import { MenuItem, MenuItemAction, MenuItemDivider } from 'menu/MenuItem';
import { hasPrimaryModifier, scrollIntoViewIfNeeded } from 'utils';
import { trackMiscAction } from 'analytics';

export interface FrameListItemProps {
	frame: Frame;
}
const FrameListItem = ({ frame }: FrameListItemProps) => {
	const dispatch = useDispatch();

	const ref = useRef() as React.MutableRefObject<HTMLDivElement>;

	const { selectedFrameIds, isSelected } = useSelector((r: RootReducer) => {
		const selectedFrameIds = getFrameIds(r.mode.selected);
		const isSelected = selectedFrameIds.includes(frame.id);
		return {
			selectedFrameIds,
			isSelected,
		};
	}, shallowEqual);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	useEffect(() => {
		if (isSelected) scrollIntoViewIfNeeded(ref);
	}, [isSelected]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onButtonClick = (e: React.MouseEvent, frame: Frame) => {
		if (e.button !== 0) return; // Only handle left-clicks
		const pick = new Pick(frame.id);
		dispatch(hasPrimaryModifier(e) ? addToSelected(pick) : setSelected(pick));
	};

	const onChatButtonClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent triggering the frame selection
		if (frame?.metadata?.sourceChatId) {
			MenuItemAction.createScrollToFrameInChat(frame.metadata.sourceChatId, frame.id).callback();
		}
	};

	const onContextMenu = async (e: React.MouseEvent, frame: Frame) => {
		if (e.target instanceof HTMLElement) {
			const menuItems: MenuItem[] = [
				MenuItemAction.createCenterFrame(frame),
				MenuItemAction.createRenameFrame(frame),
				new MenuItemDivider(),
				MenuItemAction.createCopyFrameUrl(frame),
				MenuItemAction.createCopyFrameImage(frame),
				MenuItemAction.createCopyFrameSvg(frame),
				MenuItemAction.createCopyFrameHtml(frame),
				new MenuItemDivider(),
				MenuItemAction.createDeleteFrames([frame.id]),
			];
			dispatch(
				showContextMenu({
					point: { x: e.clientX + 5, y: e.clientY },
					menuItems,
				})
			);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	return (
		<Base.Entry
			ref={ref}
			selected={selectedFrameIds.includes(frame.id)}
			onMouseDown={(e) => onButtonClick(e, frame)}
			onContextMenu={(e) => onContextMenu(e, frame)}
			onDoubleClick={() => {
				trackMiscAction('double_click_frame_left_panel');
				MenuItemAction.createCenterFrame(frame).callback();
			}}
			key={frame.id}
		>
			<S.EntryContainer>
				<S.EntryContent>
					<Base.EntryText>{frame.title}</Base.EntryText>
				</S.EntryContent>
				{/* I set up this action button but the scrolling was messed up. leaving the code here in case later i decide it's worth more time */}
				{/* {frame?.metadata?.sourceChatId && (
					<S.ActionsSection>
						<S.ActionButton onClick={onChatButtonClick} title="Show in chat">
							<Icon type={IconType.CHAT} width={12} height={12} color={theme.gray[1000]} />
						</S.ActionButton>
					</S.ActionsSection>
				)} */}
			</S.EntryContainer>
		</Base.Entry>
	);
};

export default FrameListItem;
